const GUEST_ID_KEY = "guestId";
const STORAGE_TEST_KEY = "__platter_storage_test__";
const PLATTER_STORAGE_VERSION_KEY = "platterStorageVersion";
const PLATTER_STORAGE_VERSION = "2026-05-26";
const STALE_UI_KEYS = [
  "platterProductView",
  "selectedCategory",
  "platterSelectedCategory",
  "productSearchCache",
  "platterProductSearchCache",
  "checkoutDraft",
];
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let runtimeGuestId = "";
let storageStatusLogged = false;
let runtimeWarningLogged = false;
let uiStorageVersionChecked = false;

function createGuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function getStorage(storageName) {
  try {
    if (typeof window === "undefined" || !window[storageName]) {
      return null;
    }

    const storage = window[storageName];
    storage.setItem(STORAGE_TEST_KEY, "1");
    storage.removeItem(STORAGE_TEST_KEY);
    return storage;
  } catch (error) {
    return null;
  }
}

export function getStorageStatus() {
  const localStorageRef = getStorage("localStorage");
  const sessionStorageRef = getStorage("sessionStorage");

  const status = {
    localStorageAvailable: Boolean(localStorageRef),
    sessionStorageAvailable: Boolean(sessionStorageRef),
    localStorage: localStorageRef,
    sessionStorage: sessionStorageRef,
  };

  if (!storageStatusLogged) {
    console.log("localStorage available?", status.localStorageAvailable);
    console.log("sessionStorage available?", status.sessionStorageAvailable);
    storageStatusLogged = true;
  }

  return status;
}

function readStorageValue(storage, key) {
  try {
    return storage?.getItem(key) || "";
  } catch (error) {
    return "";
  }
}

function writeStorageValue(storage, key, value) {
  try {
    storage?.setItem(key, value);
    return Boolean(storage);
  } catch (error) {
    return false;
  }
}

function removeStorageValue(storage, key) {
  try {
    storage?.removeItem(key);
  } catch (error) {
    // Ignore storage cleanup failures in restricted browsers.
  }
}

function warnRuntimeGuestSession() {
  if (!runtimeWarningLogged) {
    console.warn("Firefox privacy settings restricted browser storage. Temporary guest session enabled.");
    runtimeWarningLogged = true;
  }
}

export function isValidGuestId(value) {
  return GUID_PATTERN.test(String(value || ""));
}

function logGuestIdSource(source, guestId, wasReset = false) {
  console.log("storage type used:", source);
  console.log("current GuestId:", guestId);
  console.log("GuestId was kept/reset:", wasReset ? "reset" : "kept");
}

export function getGuestId() {
  const status = getStorageStatus();
  const localGuestId = readStorageValue(status.localStorage, GUEST_ID_KEY);
  if (localGuestId) {
    if (!isValidGuestId(localGuestId)) {
      console.warn("Invalid GuestId found in localStorage. Resetting guest session id only.");
      removeStorageValue(status.localStorage, GUEST_ID_KEY);
      const nextGuestId = setGuestId(createGuid());
      logGuestIdSource("localStorage", nextGuestId, true);
      return nextGuestId;
    }

    runtimeGuestId = localGuestId;
    logGuestIdSource("localStorage", localGuestId);
    return localGuestId;
  }

  const sessionGuestId = readStorageValue(status.sessionStorage, GUEST_ID_KEY);
  if (sessionGuestId) {
    if (!isValidGuestId(sessionGuestId)) {
      console.warn("Invalid GuestId found in sessionStorage. Resetting guest session id only.");
      removeStorageValue(status.sessionStorage, GUEST_ID_KEY);
      const nextGuestId = setGuestId(createGuid());
      logGuestIdSource("sessionStorage", nextGuestId, true);
      return nextGuestId;
    }

    runtimeGuestId = sessionGuestId;
    logGuestIdSource("sessionStorage", sessionGuestId);
    return sessionGuestId;
  }

  if (runtimeGuestId && isValidGuestId(runtimeGuestId)) {
    warnRuntimeGuestSession();
    logGuestIdSource("memory", runtimeGuestId);
    return runtimeGuestId;
  }

  const nextGuestId = createGuid();
  setGuestId(nextGuestId);
  logGuestIdSource("generated", nextGuestId, true);
  return nextGuestId;
}

export function setGuestId(guestId) {
  const nextGuestId = guestId || createGuid();
  const status = getStorageStatus();

  if (writeStorageValue(status.localStorage, GUEST_ID_KEY, nextGuestId)) {
    runtimeGuestId = nextGuestId;
    console.log("storage type used:", "localStorage");
    console.log("generated guestId:", nextGuestId);
    return nextGuestId;
  }

  if (writeStorageValue(status.sessionStorage, GUEST_ID_KEY, nextGuestId)) {
    runtimeGuestId = nextGuestId;
    console.log("storage type used:", "sessionStorage");
    console.log("generated guestId:", nextGuestId);
    return nextGuestId;
  }

  runtimeGuestId = nextGuestId;
  warnRuntimeGuestSession();
  console.log("storage type used:", "memory");
  console.log("generated guestId:", nextGuestId);
  return nextGuestId;
}

export function clearGuestId() {
  const status = getStorageStatus();
  removeStorageValue(status.localStorage, GUEST_ID_KEY);
  removeStorageValue(status.sessionStorage, GUEST_ID_KEY);
  runtimeGuestId = "";
  runtimeWarningLogged = false;
}

export function safeGetItem(key, fallback = "") {
  const status = getStorageStatus();
  return readStorageValue(status.localStorage, key) || readStorageValue(status.sessionStorage, key) || fallback;
}

export function safeSetItem(key, value) {
  const status = getStorageStatus();
  return (
    writeStorageValue(status.localStorage, key, value) ||
    writeStorageValue(status.sessionStorage, key, value)
  );
}

export function safeRemoveItem(key) {
  const status = getStorageStatus();
  removeStorageValue(status.localStorage, key);
  removeStorageValue(status.sessionStorage, key);
}

export function clearPlatterUiStorageKeys() {
  STALE_UI_KEYS.forEach((key) => safeRemoveItem(key));
}

export function initializePlatterUiStorageVersion() {
  if (uiStorageVersionChecked) {
    return;
  }

  uiStorageVersionChecked = true;
  const currentVersion = safeGetItem(PLATTER_STORAGE_VERSION_KEY);
  if (currentVersion === PLATTER_STORAGE_VERSION) {
    return;
  }

  clearPlatterUiStorageKeys();
  clearInvalidCheckoutSelection();
  safeSetItem(PLATTER_STORAGE_VERSION_KEY, PLATTER_STORAGE_VERSION);
  console.log("Platter UI/cache storage version updated. GuestId kept.");
}

export function clearInvalidCheckoutSelection() {
  const savedSelection = safeGetItem("guestCheckoutSelection");
  if (!savedSelection) {
    return;
  }

  try {
    const selection = JSON.parse(savedSelection);
    if (selection?.pickup?.pickupId) {
      return;
    }
  } catch (error) {
    // Invalid JSON means the saved pickup selection cannot be restored safely.
  }

  safeRemoveItem("guestCheckoutSelection");
  console.log("Invalid pickup selection cache cleared. GuestId kept.");
}

export function startFreshGuestSession() {
  clearGuestId();
  clearPlatterUiStorageKeys();
  const nextGuestId = getGuestId();
  console.log("GuestId was kept/reset:", "reset by user");
  return nextGuestId;
}
